import type { HairlineFigure } from "./Hairline";
import {
  Cam,
  clamp,
  extremes,
  facing,
  fit,
  poly,
  prism,
  proj,
  ringAt,
  rings,
  seg,
  unproj,
} from "./iso";
import { tdone, tset, tval, tween } from "./motion";
import {
  disposer,
  flatDot,
  mk,
  place,
  pointer,
  put,
  reflect,
  register,
  solid,
  type FigureEls,
  type FigureHandle,
} from "./stage";

/**
 * Drafts: a clipboard holding a ruled sheet of six rows, an entry slip for
 * each. A slip still a draft hovers over its row, dashed guides dropping to
 * the place it would land; a confirmed one lies flat in its row. Down the
 * board the pointer confirms: every row down to the pointer's settles, the
 * rows below it stay drafts, the motion spreading out from the pointer's row
 * on the 700ms lift curve. The slip at the pointer's row is bright. At rest
 * three rows are confirmed and the next draft, bright, waits for you. The
 * slider is how high the drafts hover.
 *
 * The pattern: one of many, as Riffle. Tweens of each slip's height, a stagger
 * by distance from the pointer's row, and a hit test on a fixed plane over
 * the sheet, read by world coordinate down the board.
 */

const N = 6,
  PITCH = 14,
  SW = 11,
  X_ROW = 30,
  Y0 = 14,
  Y1 = 72,
  ZS = 3.4,
  TH = 1.2,
  ZHIT = ZS + 8;
/** Where row k's slip lies along the board. */
const xr = (k) => X_ROW + k * PITCH;
/** The share of the hover height each row's draft hovers at, so the drafts never stand in a level row. */
const LIFT = [0.75, 0.95, 0.8, 0.7, 1, 0.8];
/** At rest: how many rows are confirmed. The next one is the bright draft. */
const REST = 3;

function mount({ stage, svg, read }: FigureEls, value: number): FigureHandle {
  const bag = disposer();
  let hover = value;

  const C = Cam(45, 0.5, 1.9);
  fit(
    C,
    [
      [-2, 0, 0],
      [122, 86, -10],
      [122, 0, 0],
      [-2, 86, 0],
      [xr(0), Y0, ZS + 30 + TH],
      [xr(N - 1) + SW, Y0, ZS + 30 + TH],
    ],
    200,
    166,
  );
  const P = proj(C),
    front = facing(C);

  const g = mk("g", {}, svg);
  const [board, boardIn] = rings(-2, 0, 122, 86, 7, 1.6);
  reflect(svg, g, P, front, board, 0, 14);
  put(solid(g), prism(P, front, board, boardIn, 0, 3));
  const [sheet, sheetIn] = rings(8, 7, 116, 79, 1.5, 0.6);
  put(solid(g), prism(P, front, sheet, sheetIn, 3, ZS));
  // The sheet's ruling: a header under the clip, then a line between each row.
  const rules = [26, ...Array.from({ length: N + 1 }, (_, k) => xr(k) - 1.5)];
  mk(
    "path",
    {
      d: rules.map((x) => seg(P(x, 12, ZS), P(x, 74, ZS))).join(""),
      class: "nf lo",
    },
    g,
  );

  // The clip at the head: its plate over the sheet's edge, the lever on it, and two rivets.
  const [cb, cbIn] = rings(2, 26, 18, 60, 4, 1.2);
  put(solid(g), prism(P, front, cb, cbIn, ZS, 6.5));
  const [lv, lvIn] = rings(4, 31, 14, 55, 5, 1);
  put(solid(g), prism(P, front, lv, lvIn, 6.5, 10));
  for (const y of [29, 57]) place(flatDot(g, C, 0.7, "dot m"), P(15.5, y, 6.5));

  // Row by row down the board: a nearer row is wholly nearer, so this order holds
  // in every pose. Each row's landing and guides go before its slip, which covers them.
  const slips = LIFT.map((f, k) => {
    const [ring, crease] = rings(xr(k), Y0, xr(k) + SW, Y1, 2, 0.8);
    const landing = mk("path", { class: "nf dash" }, g);
    const guides = mk("path", { class: "nf dash" }, g);
    const el = solid(g),
      marks = mk("path", { class: "nf lo" }, el.g);
    return {
      k,
      ring,
      crease,
      landing,
      guides,
      el,
      marks,
      z: tween(k < REST ? 0 : hover * f),
      drawn: NaN,
    };
  });

  /** A slip lifted z above its row: its body, its two lines, and, while it hovers, its landing and guides. */
  function draw(s, z) {
    if (z === s.drawn) return;
    s.drawn = z;
    const z0 = ZS + z,
      x = xr(s.k) + SW / 2;
    put(s.el, prism(P, front, s.ring, s.crease, z0, z0 + TH));
    s.marks.setAttribute(
      "d",
      seg(P(x, Y0 + 6, z0 + TH), P(x, Y0 + 36, z0 + TH)) +
        seg(P(x, Y1 - 12, z0 + TH), P(x, Y1 - 4, z0 + TH)),
    );
    const up = z > 0.6;
    s.landing.setAttribute("d", up ? poly(ringAt(P, s.ring, ZS)) : "");
    s.guides.setAttribute(
      "d",
      up
        ? extremes(P, s.ring)
            .map((q) => seg(P(q.u, q.v, z0), P(q.u, q.v, ZS)))
            .join("")
        : "",
    );
  }

  const B = register(stage, (_dt, now) => {
    let moving = false;
    for (const s of slips) {
      draw(s, tval(s.z, now));
      if (!tdone(s.z, now)) moving = true;
    }
    return moving;
  });
  bag.add(() => B.unregister());

  /** How many rows a pointer confirms: every row down to the one it is over, on a plane over the sheet; -1 off the board. */
  function hit([sx, sy]) {
    const [wx, wy] = unproj(C, sx, sy, ZHIT);
    if (wy < -6 || wy > 92 || wx < -6 || wx > 128) return -1;
    return wx < xr(0) ? 0 : clamp(Math.floor((wx - xr(0)) / PITCH) + 1, 0, N);
  }

  let act = -1;
  /** Confirms the first c rows (-1 puts the rest back): settling, or lifting, later the further from the pointer's row. */
  function setActive(c) {
    if (c === act) return;
    const now = performance.now(),
      n = c < 0 ? REST : c,
      from = Math.max(0, (c < 0 ? act : c) - 1);
    act = c;
    for (const s of slips) {
      tset(
        s.z,
        s.k < n ? 0 : hover * LIFT[s.k],
        now,
        Math.abs(s.k - from) * 45,
      );
      s.el.sil.classList.toggle(
        "hi",
        c < 0 ? s.k === REST : s.k === Math.max(0, c - 1),
      );
    }
    read.textContent = c < 0 ? "rest" : `${c} of ${N}`;
    B.wake();
  }

  slips[REST].el.sil.classList.add("hi");
  bag.add(
    pointer(stage, {
      move: (p) => setActive(hit(p)),
      leave: () => setActive(-1),
    }),
  );
  bag.add(() => svg.replaceChildren());

  return {
    set: (v) => {
      hover = v;
      const c = act;
      act = -2;
      setActive(c);
    },
    destroy: () => bag.dispose(),
  };
}

export const DRAFTS: HairlineFigure = {
  name: "drafts",
  means:
    "A clipboard of six entries: drafts hover over their rows, and down the board the pointer settles each one into place.",
  range: [12, 20, 28],
  tour: [[242, 173], [146, 125], [186, 145], null],
  mount,
};
